import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import SemiCirclePieChart from './SemiCirclePieChart';

const safeNumber = (value, defaultValue = 0) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  return !isNaN(parsed) ? parsed : defaultValue;
};

const formatPercentage = (value) => {
  const num = safeNumber(value);
  return Math.round(num);
};

// Utility function to render passages with enhanced styling and key phrase emphasis
const renderEnhancedPassage = (passage, themeColor = '#3b82f6') => {
  return (
    <div className="space-y-4 sm:space-y-6">
      {passage.split('\n\n').map((paragraph, index) => (
        <p key={index} className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-xl break-words">
          {paragraph}
        </p>
      ))}
    </div>
  );
};

// CSS styles for key phrase emphasis
const keyPhraseStyles = {
  performance: {
    textDecoration: 'underline',
    textDecorationColor: '#3b82f6',
    textDecorationThickness: '2px',
    textUnderlineOffset: '3px',
    fontWeight: '500'
  },
  mastery: {
    textDecoration: 'underline',
    textDecorationColor: '#22c55e',
    textDecorationThickness: '2px',
    textUnderlineOffset: '3px',
    fontWeight: '500'
  },
  attainment: {
    textDecoration: 'underline',
    textDecorationColor: '#f59e0b',
    textDecorationThickness: '2px',
    textUnderlineOffset: '3px',
    fontWeight: '500'
  }
};

const generateNormalCurve = (mean = 50, stdDev = 15, points = 100) => {
  const curve = [];
  const minX = 0;
  const maxX = 100;
  const step = (maxX - minX) / points;

  for (let i = 0; i <= points; i++) {
    const x = minX + (i * step);
    const y = (1 / (stdDev * Math.sqrt(2 * Math.PI))) *
              Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2));
    curve.push({ x, y });
  }

  const maxY = Math.max(...curve.map(point => point.y));
  return curve.map(point => ({
    x: point.x,
    y: (point.y / maxY) * 80 // Scale to 80% of chart height
  }));
};

/**
 * GoalSettingSection - Specialized component for displaying goal setting construct data
 *
 * This component provides a unique UI for the Goal Setting construct, featuring:
 * - First card: Semi-circle pie chart showing Mastery vs Performance continuum with personalized passages
 * - Second card: Goal Attainment with normal distribution graph and personalized passages
 *
 * The component safely handles data extraction from Redux contextData and provides
 * fallback values for missing or invalid data.
 *
 * @returns {JSX.Element} The goal setting dashboard with semi-circle pie chart and goal attainment sections
 */
const GoalSettingSection = () => {
  const { contextData } = useSelector((state) => state.studentPerformenceConstruct);

  const goalSettingData = useMemo(() => {


    const dataSource = contextData ;
    if (!dataSource) return null;

    const constructs = dataSource.constructs || {};
    const features = dataSource.features?.Stack_GoalSetting || {};
    const { construct_label = [] } = dataSource;

    const selfLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('goalsetting') &&
      item.behavior_graph_key?.toLowerCase().includes('self')
    );
    const othersLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('goalsetting') &&
      item.behavior_graph_key?.toLowerCase().includes('others')
    );
    const attainmentLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('goalsetting') &&
      item.behavior_graph_key?.toLowerCase().includes('attainment')
    );

    const hasSelfData = selfLabel?.percentage !== undefined || constructs.Stack_GoalSetting_Self !== undefined || features['Self Goal Setting']?.percentage !== undefined;
    const hasOthersData = othersLabel?.percentage !== undefined || constructs.Stack_GoalSetting_Others !== undefined || features['Others Goal Setting']?.percentage !== undefined;
    const hasAttainmentData = attainmentLabel?.percentage !== undefined || constructs.Stack_GoalSetting_Attainment !== undefined || features['Goals Attained']?.percentage !== undefined;

    if (!hasSelfData && !hasOthersData && !hasAttainmentData) {
      return null; 
    }

    const selfGoalSetting = safeNumber(
      constructs.Stack_GoalSetting_Self ||
      features['Self Goal Setting']?.percentage ||
      selfLabel?.percentage
    );
    const othersGoalSetting = safeNumber(
      constructs.Stack_GoalSetting_Others ||
      features['Others Goal Setting']?.percentage ||
      othersLabel?.percentage
    );
    const goalAttainment = safeNumber(
      constructs.Stack_GoalSetting_Attainment ||
      features['Goals Attained']?.percentage ||
      attainmentLabel?.percentage
    );

    const performanceOrientation = othersGoalSetting;
    const masteryOrientation = selfGoalSetting;

    const goalSettingFeatures = {};
    if (features && Object.keys(features).length > 0) {
      Object.entries(features).forEach(([key, value]) => {
        goalSettingFeatures[key] = {
          percentage: safeNumber(value?.percentage || value),
          raw_value: value?.raw_value || value?.value || value,
          human_readable_name: value?.human_readable_name || key.replace(/_/g, ' ')
        };
      });
    }

    return {
      selfGoalSetting,
      othersGoalSetting,
      goalAttainment,
      performanceOrientation,
      masteryOrientation,
      features: goalSettingFeatures
    };
  }, [contextData]);

  const getPerformancePassageContent = (percentile) => {
    if (percentile >= 67) {
      return {
        category: 'Low Mastery (High Performance)',
        color: '#3b82f6',
        passage: `Your goal-setting orientation leans heavily toward performance, where external recognition and outcomes drive your motivation. You tend to focus on achieving tangible results rather than striving for personal mastery or growth. When faced with challenges or setbacks, your primary concern is often meeting external expectations, which can sometimes make it harder for you to persist through difficult tasks that focus on skill development. While you tend to perform well in more straightforward tasks, your focus on efficiency over deeper learning can limit your opportunities for personal growth. Shifting some of your focus toward intrinsic mastery, and allowing yourself to engage more deeply with challenges, could lead to more sustainable, long-term growth in your abilities.`
      };
    } else if (percentile >= 34) {
      return {
        category: 'Mid Mastery (Mid Performance)',
        color: '#22c55e',
        passage: `You demonstrate a balanced approach to goal setting, where both performance and mastery play significant roles in motivating you. You find value in achieving high standards and gaining external recognition, but you also appreciate the personal growth and skills that come from the learning process. 
        
        While you may occasionally face internal conflict between these two motivations, you generally manage to navigate this balance effectively. This allows you to pursue tasks with both a sense of excellence and a desire to improve yourself. Striving for high performance while ensuring your focus on personal mastery can lead to sustained success in both achieving goals and developing your skills.`
      };
    } else {
      return {
        category: 'High Mastery (Low Performance)',
        color: '#22c55e',
        passage: `You are deeply focused on personal mastery, driven by an intrinsic motivation to improve and grow rather than by external validation or rewards. Your goals are primarily centered on acquiring new skills and refining your abilities, and you view setbacks as valuable learning opportunities. External performance benchmarks are not a significant motivator for you; instead, you take pride in the progress you make and the expertise you develop. This focus on mastery allows you to persist through challenges, continually striving to improve without the pressure of meeting external expectations. As a result, you are likely to experience long-term growth, as your commitment to personal development outweighs the need for immediate recognition or success.`
      };
    }
  };

  const getAttainmentPassageContent = (percentile) => {
    if (percentile >= 67) {
      return {
        category: 'High Goal Attainment',
        color: '#22c55e',
        passage: `You consistently set and achieve high goals, driven by a strong sense of determination and focus. Your commitment to your objectives is evident as you maintain a clear path toward success, even when faced with challenges. You rarely lower your goals after setbacks; instead, you see failures as opportunities for growth, reattempting challenges with persistence and an unwavering commitment to achieving your targets. Your ability to maintain high standards for yourself, along with your resilience in overcoming obstacles, ensures that you continue to meet and exceed the goals you set. Your high goal attainment is a testament to your exceptional focus, drive, and perseverance, allowing you to consistently succeed in even the most demanding situations.`
      };
    } else if (percentile >= 34) {
      return {
        category: 'Average Goal Attainment',
        color: '#3b82f6',
        passage: `You demonstrate a moderate level of goal attainment, where you set realistic and achievable goals and often succeed in meeting them. However, you sometimes face challenges that require you to adjust your goals or plans along the way. While you may not always meet your targets on the first try, your ability to revisit and retry goals shows a healthy balance of persistence and adaptability. Your flexibility in adjusting goals when necessary helps you stay motivated and focused, ensuring continued progress despite occasional setbacks. To further enhance your goal attainment, maintaining focus on long-term objectives while embracing small adjustments as needed can support even greater success.`
      };
    } else {
      return {
        category: 'Low Goal Attainment',
        color: '#f59e0b',
        passage: `You tend to struggle with goal attainment, often setting ambitious goals that may not align with your current abilities or failing to follow through with your plans. This tendency to adjust or lower goals when faced with obstacles reflects a lack of persistence in the face of challenges. Rather than maintaining consistent focus on personal targets, you may find yourself frequently shifting your goals based on external expectations or frustration. To enhance your goal attainment, focusing on setting realistic, achievable targets and persisting through setbacks will help build your ability to accomplish more over time.`
      };
    }
  };

  if (!goalSettingData) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading goal setting data...</p>
        </div>
      </div>
    );
  }

  const { performanceOrientation, goalAttainment } = goalSettingData || {};
  const performancePassageInfo = getPerformancePassageContent(performanceOrientation || 0);
  const attainmentPassageInfo = getAttainmentPassageContent(goalAttainment || 0);
  const curvePoints = generateNormalCurve();

  const createCurvePath = (points) => {
    if (points.length === 0) return '';

    let path = `M ${points[0].x} ${100 - points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      path += ` L ${points[i].x} ${100 - points[i].y}`;
    }
    return path;
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-8">
      {/* First Card: Mastery vs Performance Dial */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left side - Semi-Circle Pie Chart */}
        <div className="w-full md:w-1/2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg sm:text-xl md:!text-2xl font-semibold text-black mb-4 sm:!mb-4 text-center">Goal Setting Orientation</h3>

          {/* Semi-Circle Pie Chart Container */}
          <div className="relative flex flex-col items-center shadow-md">
            <SemiCirclePieChart
              masteryPercentage={formatPercentage(goalSettingData?.masteryOrientation || 50)}
              performancePercentage={formatPercentage(goalSettingData?.performanceOrientation || 50)}
              chartId="goal-setting-semi-circle-chart"
            />


          </div>

          {/* Percentile Explanation */}
          <div className="mt-4 sm:mt-6 w-full p-3 bg-[#0000000A] rounded-lg border border-gray-200">
            <p className="text-xs sm:text-sm md:text-md text-gray-700 leading-relaxed">
              <strong>About Percentiles:</strong> Percentiles show how your score compares to a nationally representative sample of U.S. adults. For example, if you score in the 75th percentile, you scored higher than 75% of adults in the U.S.
            </p>
          </div>
        </div>

        {/* Right side - Performance Passage */}
        <div className="w-full md:w-1/2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="w-5 h-5 rounded mr-3" style={{ backgroundColor: performancePassageInfo.color }}></div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-black">{performancePassageInfo.category}</h3>
          </div>

          <div className="prose prose-sm max-w-none h-full flex flex-col justify-start">
            {renderEnhancedPassage(performancePassageInfo.passage, performancePassageInfo.color)}
          </div>
        </div>
      </div>

      {/* Second Card: Goal Attainment */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left side - Normal Distribution Chart */}
        <div className="w-full md:w-1/2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-lg sm:text-xl md:!text-2xl font-semibold text-black mb-4 sm:!mb-4 text-center">Goal Attainment Distribution</h3>

          {/* Chart Container */}
          <div className="relative">
            {/* SVG Bell Curve */}
            <div className="relative h-80 border-2 border-gray-400 bg-white">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                {/* Bell curve */}
                <path
                  d={createCurvePath(curvePoints)}
                  fill="rgba(59, 130, 246, 0.2)"
                  stroke="rgba(59, 130, 246, 0.8)"
                  strokeWidth="0.5"
                />

                {/* Percentile line */}
                <line
                  x1={goalAttainment || 0}
                  y1="20"
                  x2={goalAttainment || 0}
                  y2="100"
                  stroke="red"
                  strokeWidth="0.8"
                  strokeDasharray="2,2"
                />
              </svg>

              {/* X-axis labels */}
              <div className="absolute -bottom-2 left-0 w-full flex justify-between text-xs sm:text-sm md:text-lg text-gray-600 transform translate-y-6">
                <span>0</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
              </div>

              {/* Percentile label positioned above the red line */}
              <div
                className="absolute top-4 transform -translate-x-1/2"
                style={{ left: `${goalAttainment || 0}%` }}
              >
                <div className="text-xs sm:text-sm md:text-lg text-red-600 font-bold bg-white px-1 rounded shadow-sm">
                  {formatPercentage(goalAttainment || 0)}%
                </div>
              </div>
            </div>

            {/* X-axis label */}
            <div className="text-center mt-8">
              <div className="text-xs sm:text-sm md:text-lg font-medium text-gray-700">
                Goal Attainment Percentile
              </div>
            </div>
          </div>

          {/* Raw Values Display */}
          <div className="mt-6">
            <div className="bg-green-50 p-3 rounded text-center">
              <div className="text-sm sm:text-lg font-medium text-gray-600 text-nowrap">Your Goal Attainment Score</div>
              <div className="text-lg sm:text-2xl font-bold text-green-600">
                {formatPercentage(goalAttainment || 0)}th percentile
              </div>


            </div>
          </div>

          {/* Percentile Explanation */}
          <div className="mt-4 sm:mt-6 w-full p-3 bg-[#0000000A] rounded-lg border border-gray-200">
            <p className="text-xs sm:text-sm md:text-md text-gray-700 leading-relaxed">
              <strong>About Percentiles:</strong> Percentiles show how your score compares to a nationally representative sample of U.S. adults. For example, if you score in the 75th percentile, you scored higher than 75% of adults in the U.S.
            </p>
          </div>
        </div>

        {/* Right side - Goal Attainment Passage */}
        <div className="w-full md:w-1/2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="w-5 h-5 rounded mr-3" style={{ backgroundColor: attainmentPassageInfo.color }}></div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-black">{attainmentPassageInfo.category}</h3>
          </div>

          <div className="prose prose-sm max-w-none h-full flex flex-col justify-start">
            {renderEnhancedPassage(attainmentPassageInfo.passage, attainmentPassageInfo.color)}
          </div>

        </div>
      </div>
      </div>
    </div>
  );
};

export default GoalSettingSection;
